import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { BellIcon, ChevronDownIcon, IdCardIcon, MailIcon, MapPinIcon, MenuIcon, SearchIcon, SmartphoneIcon, UserRoundIcon, XIcon } from 'lucide-react';
import { Logo } from './Logo';
import { LanguageToggle } from './LanguageToggle';
import { TextSizeToggle } from './TextSizeToggle';
import { BellPreview } from './avis/BellPreview';
import { AlertesDialog } from './avis/AlertesDialog';
import { HeaderTooltip } from './HeaderTooltip';
import { useAuth } from '../contexts/AuthContext';
import { navItems } from '../data/navigation';

/** Raccourcis bciti+ repris dans le tiroir mobile. */
const bcitiActions = [{
  label: 'Mon espace',
  to: '/mon-espace',
  icon: SmartphoneIcon
}, {
  label: 'Mon profil',
  to: '/mon-profil',
  icon: UserRoundIcon
}, {
  label: 'Ma carte',
  to: '/ma-carte',
  icon: IdCardIcon
}, {
  label: 'Plan',
  to: '/plan',
  icon: MapPinIcon
}];
const utilityLink = 'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold text-ville-navy transition-colors duration-150 ease-ville hover:text-ville-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-blue';
const iconButton = 'group relative grid h-11 w-11 place-items-center rounded-full text-ville-navy transition-colors duration-150 ease-ville hover:bg-ville-mist hover:text-ville-deep focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-blue';

/**
 * En-tête de Beaconsfield, en deux rangées.
 *
 * Leur en-tête réel porte déjà FR/EN, le sélecteur AAA, « Nous joindre » et la loupe.
 * Empiler les cinq icônes bciti+ sur une seule ligne casserait vers 1280 px : la rangée
 * utilitaire absorbe donc ce qui est documentaire (Plan, Ma carte, langue, taille,
 * contact), la rangée principale ne gardant que le personnel et l'actionnable.
 */
export function Header() {
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const [alertesOpen, setAlertesOpen] = useState(false);
  const {
    isAuthenticated
  } = useAuth();
  return <header className="relative w-full bg-white" data-id="element-1647" data-mp="1ho1l9cj9pq56">
      {/* Rangée 1 — barre utilitaire */}
      <div className="hidden border-b border-ville-mist bg-ville-sand md:block" data-id="element-1648" data-mp="1mml5n8i7qtvf">
        <div className="mx-auto flex max-w-[1400px] items-center justify-end gap-1 px-5 py-1.5 lg:px-10" data-id="element-1649" data-mp="1mki6nyz31qek">
          <Link to="/plan" className={utilityLink} data-id="element-1650" data-mp="1cooyersecj2l">
            <MapPinIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1651" data-mp="1cpgk9aw6z2fo" />
            Plan
          </Link>
          <Link to="/ma-carte" className={utilityLink} data-id="element-1652" data-mp="yqgsvvhao9ft">
            <IdCardIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1653" data-mp="13nqu08e1q9jh" />
            Ma carte
          </Link>

          <span className="mx-2 h-4 w-px bg-ville-navy/15" aria-hidden="true" data-id="element-1654" data-mp="2t2f586awhlfx" />

          <LanguageToggle data-id="element-1655" data-mp="2y204khtzfvt6" />
          <TextSizeToggle data-id="element-1656" data-mp="2y29q9t7zvgt2" />

          <a href="#accueil" className="ml-2 inline-flex items-center gap-2 rounded-full border border-ville-navy/25 px-4 py-1.5 text-xs font-bold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-navy hover:bg-ville-navy hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-blue" data-id="element-1657" data-mp="2y1hzqwt9899n">
            Nous joindre
            <MailIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1658" data-mp="rc6e2tganqsw" />
          </a>
        </div>
      </div>

      {/* Rangée 2 — logo, menus, services personnels */}
      <div className="mx-auto flex max-w-[1400px] items-center gap-6 px-5 py-4 lg:px-10" onMouseLeave={() => setOpenMenu(null)} data-id="element-1659" data-mp="106umgsh61olx">
        <Link to="/" className="rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-blue" data-id="element-1660" data-mp="1f1968v8k121n">
          <Logo data-id="element-1661" data-mp="3guozlzw10v0b" />
        </Link>

        <nav aria-label="Navigation principale" className="hidden lg:block" data-id="element-1662" data-mp="3gtx4eq6cczak">
          <ul className="flex items-center gap-2" data-id="element-1663" data-mp="3bvvjdzpcupwg">
            {navItems.map(item => {
            const isOpen = openMenu === item.label;
            return <li key={item.label} data-id="element-1664" data-mp="230rnpzjulmcg">
                  <button type="button" aria-expanded={isOpen} onMouseEnter={() => setOpenMenu(item.label)} onClick={() => setOpenMenu(isOpen ? null : item.label)} className={`flex items-center gap-2 rounded-full px-4 py-2 text-[15px] font-semibold transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-blue ${isOpen ? 'text-ville-blue' : 'text-ville-navy hover:text-ville-blue'}`} data-id="element-1665" data-mp="27x8njr0etp5d">
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-ville-lime" aria-hidden="true" data-id="element-1666" data-mp="1y041dr6kdtok" />
                    {item.label}
                    <ChevronDownIcon className={`h-4 w-4 transition-transform duration-150 ease-ville ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-1667" data-mp="11h6cn5r900bs" />
                  </button>
                </li>;
          })}
          </ul>
        </nav>

        <div className="ml-auto flex items-center gap-1.5" data-id="element-1668" data-mp="sykzwqtttvy2">
          <div className="relative hidden md:block" data-id="element-1669" data-mp="oxr8vol3810y">
            <button type="button" aria-label={isAuthenticated ? 'Mes notifications, 3 non lus dont une alerte urgente' : 'S’inscrire aux alertes citoyennes'} aria-expanded={isAuthenticated ? bellOpen : alertesOpen} onClick={() => isAuthenticated ? setBellOpen(v => !v) : setAlertesOpen(true)} className={iconButton} data-id="element-1670" data-mp="jzzgyukg0ewr">
              <BellIcon className="h-5 w-5" aria-hidden="true" data-id="element-1671" data-mp="1bcb49ufzli4" />
              <span className="absolute -right-0.5 -top-0.5 grid h-5 min-w-[20px] place-items-center rounded-full bg-ville-coral px-1 text-[11px] font-bold text-white ring-2 ring-white" aria-hidden="true" data-id="element-1672" data-mp="3serbwb04tlhu">
                {isAuthenticated ? '3' : '!'}
              </span>
              {!bellOpen && <HeaderTooltip label={isAuthenticated ? 'Mes notifications' : 'Alertes citoyennes'} data-id="element-1673" data-mp="3ipu71axwt8jc" />}
            </button>
            <AnimatePresence data-id="element-1674" data-mp="38t7mkn9hwjhu">
              {isAuthenticated && bellOpen && <BellPreview onNavigate={() => setBellOpen(false)} data-id="element-1675" data-mp="e7dzcpv03ki" />}
            </AnimatePresence>
          </div>

          <Link to="/mon-espace" aria-label="Mon espace" className={`hidden md:grid ${iconButton}`} data-id="element-1676" data-mp="5d0rh555buko">
            <SmartphoneIcon className="h-5 w-5" aria-hidden="true" data-id="element-1677" data-mp="3mu1smz92m8dc" />
            <HeaderTooltip label="Mon espace" data-id="element-1678" data-mp="3mrpfr0i66o75" />
          </Link>

          <Link to="/mon-profil" aria-label="Mon profil" className={`hidden md:grid ${iconButton}`} data-id="element-1679" data-mp="3rrk6n41y3ds7">
            <UserRoundIcon className="h-5 w-5" aria-hidden="true" data-id="element-1680" data-mp="jc9z3gfadgd1" />
            <HeaderTooltip label="Mon profil" data-id="element-1681" data-mp="o4bidmavg7ck" />
          </Link>

          <button type="button" aria-label="Rechercher sur le site" className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-ville-mist text-ville-navy transition-colors duration-150 ease-ville hover:bg-ville-leaf hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-blue" data-id="element-1682" data-mp="o5mkziskz923">
            <SearchIcon className="h-5 w-5" aria-hidden="true" data-id="element-1683" data-mp="2dm8c1gkt8zjd" />
          </button>

          <button type="button" aria-label={mobileOpen ? 'Fermer le menu' : 'Ouvrir le menu'} aria-expanded={mobileOpen} onClick={() => setMobileOpen(v => !v)} className={`lg:hidden ${iconButton}`} data-id="element-1684" data-mp="1pwe0r3g53j18">
            {mobileOpen ? <XIcon className="h-5 w-5" data-id="element-1685" data-mp="1utwa2uy2k09r" /> : <MenuIcon className="h-5 w-5" data-id="element-1686" data-mp="1usuovxnc9xeb" />}
          </button>
        </div>
      </div>

      {/* Méga-menu : leurs trois sections en colonnes, comme sur le site réel. */}
      {openMenu && <div className="absolute inset-x-0 top-full z-50 hidden border-t border-ville-mist bg-white shadow-xl shadow-ville-navy/10 lg:block" onMouseEnter={() => setOpenMenu(openMenu)} onMouseLeave={() => setOpenMenu(null)} data-id="element-1687" data-mp="368t3si577dng">
          <div className="mx-auto grid max-w-[1400px] gap-10 px-5 py-8 md:grid-cols-3 lg:px-10" data-id="element-1688" data-mp="1dkk12inxrpde">
            {navItems.find(item => item.label === openMenu)?.groups.map(group => <nav key={group.title} aria-label={group.title} data-id="element-1689" data-mp="1mcdl4f2obi7q">
                  <h2 className="font-display text-sm font-bold uppercase tracking-wider text-ville-blue" data-id="element-1690" data-mp="1rci5ig6pcx38">
                    {group.title}
                  </h2>
                  <ul className="mt-3 space-y-1.5" data-id="element-1691" data-mp="1wccs47jg30ih">
                    {group.links.map(link => <li key={link} data-id="element-1692" data-mp="219wgi09uwto5">
                        <a href="#accueil" className="block text-sm text-slate-600 transition-colors duration-150 ease-ville hover:text-ville-navy" data-id="element-1693" data-mp="1xttig20xh5q">
                          {link}
                        </a>
                      </li>)}
                  </ul>
                </nav>)}
          </div>
        </div>}

      {/* Tiroir mobile : les services bciti+ d'abord, les menus du site ensuite. */}
      {mobileOpen && <nav aria-label="Navigation mobile" className="border-t border-ville-mist bg-white px-5 pb-6 lg:hidden" data-id="element-1694" data-mp="26gtb39s80xqi">
          <p className="pt-4 font-display text-sm font-bold uppercase tracking-wider text-ville-blue" data-id="element-1695" data-mp="334zi23cm827y">
            Mon bciti+
          </p>
          <ul className="mt-3 grid gap-2 sm:grid-cols-2" data-id="element-1696" data-mp="2u4ude0yt3hxh">
            {bcitiActions.map(action => <li key={action.label} data-id="element-1697" data-mp="2p6j1zlsv4zd5">
                <Link to={action.to} onClick={() => setMobileOpen(false)} className="flex items-center gap-3 rounded-2xl bg-ville-mist px-4 py-3 text-[15px] font-bold text-ville-navy" data-id="element-1698" data-mp="2p5hq5f40w8ud">
                  <action.icon className="h-5 w-5" aria-hidden="true" data-id="element-1699" data-mp="1d2nxcd01v5fp" />
                  {action.label}
                </Link>
              </li>)}
          </ul>

          <div className="mt-5 flex items-center gap-3 border-t border-ville-mist pt-4" data-id="element-1700" data-mp="1820039l89sd1">
            <LanguageToggle data-id="element-1701" data-mp="1mutrntdrxyy2" />
            <TextSizeToggle data-id="element-1702" data-mp="1i2acirzfuvr5" />
          </div>

          <ul className="mt-2 divide-y divide-ville-mist" data-id="element-1703" data-mp="1i0frzes2x5nl">
            {navItems.map(item => <li key={item.label} className="py-3" data-id="element-1704" data-mp="1vrubrntt9avp">
                <p className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wide text-ville-navy" data-id="element-1705" data-mp="1qukl64uoqt6a">
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-ville-lime" aria-hidden="true" data-id="element-1706" data-mp="25qjqx8uboh89" />
                  {item.label}
                </p>
                {item.groups.map(group => <div key={group.title} className="mt-2" data-id="element-1707" data-mp="bed5lh7psk28">
                    <p className="text-xs font-bold uppercase tracking-wider text-ville-blue" data-id="element-1708" data-mp="6eqrz6nz51sw">{group.title}</p>
                    <ul className="mt-1 space-y-1" data-id="element-1709" data-mp="2i1aun7acji9b">
                      {group.links.map(link => <li key={link} data-id="element-1710" data-mp="2n16wp0zhgzm8">
                          <a href="#accueil" className="block py-1 text-[15px] text-slate-600" data-id="element-1711" data-mp="2n0466u62oz6u">
                            {link}
                          </a>
                        </li>)}
                    </ul>
                  </div>)}
              </li>)}
          </ul>
        </nav>}

      <AnimatePresence data-id="element-1712" data-mp="23dfz7j4yezsj">
        {alertesOpen && <AlertesDialog key="header-alertes" onClose={() => setAlertesOpen(false)} data-id="element-1713" data-mp="23ehoarit1mt9" />}
      </AnimatePresence>
    </header>;
}